import { useEffect, useMemo, useState } from "react";
import {
	LineChart as LineChartIcon,
	Loader2,
	ExternalLink,
	ClipboardList,
	Clock,
	Edit2,
	Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
	Radar,
	RadarChart,
	PolarGrid,
	PolarAngleAxis,
	PolarRadiusAxis,
	ResponsiveContainer,
	LineChart,
	CartesianGrid,
	XAxis,
	YAxis,
	Tooltip as RechartsTooltip,
	Line,
} from "recharts";
import {
	useChartColors,
	useChartTooltipStyle,
	useReadableColor,
} from "../lib/useChartColors";
import { useLanguage } from "../contexts/LanguageContext";
import { fetchAPI, formatDate } from "../lib/utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent } from "./ui/card";
import { SessionDetailDialog } from "./SessionDetailDialog";
import { HistoryEntrySource } from "./performance/HistoryEntrySource";
import { IpfTotalLine } from "./performance/IpfTotalLine";
import { LevelBar } from "./performance/LevelBar";
import { LevelsGuideButton } from "./performance/LevelsGuide";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";

function formatMinutesSeconds(value) {
	const totalSeconds = Math.max(0, Math.round(Number(value) || 0));
	const mins = Math.floor(totalSeconds / 60);
	const secs = totalSeconds % 60;
	return `${mins}:${String(secs).padStart(2, "0")}`;
}

function getUnitLabel(unit) {
	switch ((unit || "").toLowerCase()) {
		case "kg":
			return "kg";
		case "reps":
			return "reps";
		case "seconds":
			return "sec";
		case "minutes":
			return "min";
		case "minutes_seconds":
			return "mm:ss";
		case "meters":
			return "m";
		case "points":
			return "pt";
		case "hit_factor":
			return "HF";
		default:
			return unit || "";
	}
}

function getPerformanceSourceLabel(exercise, t) {
	if (exercise?.source === "manual") return t("manualLabel");
	if (exercise?.source === "auto") return t("fromDiary");
	return "";
}

function getPerformanceSourceMeta(exercise) {
	if (!exercise) return "";
	if (exercise.source === "manual") return exercise.manual_date || "";
	if (exercise.source === "auto") return exercise.auto_date || "";
	return "";
}

export function PerformanceProfilePreview({
	domains = [],
	loading = false,
	athleteId = null,
	// Le discipline dell'atleta, quando il chiamante le ha: servono solo a
	// distinguere il profilo vuoto "senza sport" da quello "senza risultati".
	athleteSports = null,
	// Lo sport del tab attivo, quando l'atleta ne pratica piu' di uno. Va
	// passato anche allo storico: il backend cerca la configurazione
	// dell'esercizio nello sport richiesto, e senza `?sport=` cerca nella
	// primaria — un benchmark del secondo sport rispondeva 404.
	sport = null,
	onHistoryChanged,
}) {
	const chartColors = useChartColors();
	const tooltipStyle = useChartTooltipStyle();
	/* I colori dei domini li sceglie il coach e sono tarati sul fondo
	   scuro: su fondo chiaro vanno scuriti quanto basta per leggerli. */
	const readable = useReadableColor();
	const { t } = useLanguage();
	const [activeTab, setActiveTab] = useState("results");
	const [historyOpen, setHistoryOpen] = useState(false);
	const [historyExercise, setHistoryExercise] = useState(null);
	const [historyData, setHistoryData] = useState(null);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [historyError, setHistoryError] = useState("");
	const [historyRevision, setHistoryRevision] = useState(0);
	const [sessionResultId, setSessionResultId] = useState(null);
	const [editingKey, setEditingKey] = useState(null);
	const [savingKey, setSavingKey] = useState(null);
	const [draft, setDraft] = useState({
		date: "",
		weight: "",
		reps: "",
		value: "",
		notes: "",
	});

	useEffect(() => {
		if (!domains.length) {
			setActiveTab("results");
			return;
		}
		if (activeTab === "results" || activeTab === "radar") return;
		const exists = domains.some((domain) => domain.domain_id === activeTab);
		if (!exists) setActiveTab("results");
	}, [activeTab, domains]);

	// In un modello per categoria di peso (D6b di docs/sport-config) una riga
	// senza la tabella della categoria si legge su quella del genere: il coach
	// deve vederlo, o scambia un buco del modello per una carenza dell'atleta.
	const classBased = useMemo(
		() =>
			domains.some((domain) =>
				(domain.exercises || []).some(
					(exercise) => exercise.scoring_basis === "weight_class",
				),
			),
		[domains],
	);

	const visibleDomains = useMemo(() => {
		if (activeTab === "results") return domains;
		if (activeTab === "radar") return [];
		return domains.filter((domain) => domain.domain_id === activeTab);
	}, [activeTab, domains]);

	const historyItems = useMemo(() => {
		if (!historyData) return [];
		if (Array.isArray(historyData.items)) return historyData.items;
		if (Array.isArray(historyData.entries)) return historyData.entries;
		return [];
	}, [historyData]);

	const historyChartData = useMemo(() => {
		if (!historyData?.unit) return [];
		if (historyData.unit === "kg") {
			return historyItems
				.filter(
					(item) =>
						item?.raw_weight !== null &&
						item?.raw_weight !== undefined &&
						item?.estimated_1rm !== null &&
						item?.estimated_1rm !== undefined,
				)
				.slice(0, 20)
				.reverse()
				.map((item) => ({
					date: item.date ? String(item.date).slice(5, 10) : "",
					weight: Number(item.raw_weight),
					estimated1rm: Number(item.estimated_1rm),
					reps: item.raw_reps ?? null,
				}));
		}
		return historyItems
			.filter((item) => item?.value !== null && item?.value !== undefined)
			.slice(0, 20)
			.reverse()
			.map((item) => ({
				date: item.date ? String(item.date).slice(5, 10) : "",
				value: Number(item.value),
			}));
	}, [historyData, historyItems]);

	const handleOpenHistory = (exercise) => {
		if (!exercise?.exercise_id) return;
		setHistoryExercise(exercise);
		setHistoryData(null);
		setHistoryError("");
		setHistoryOpen(true);
	};

	useEffect(() => {
		if (!historyOpen || !historyExercise?.exercise_id) return;

		let active = true;
		const loadHistory = async () => {
			setHistoryLoading(true);
			setHistoryError("");
			try {
				const encodedExerciseId = encodeURIComponent(
					historyExercise.exercise_id,
				);
				const sportQuery = sport ? `?sport=${encodeURIComponent(sport)}` : "";
				const endpoint = athleteId
					? `/coach/athletes/${athleteId}/performance-profile/history/${encodedExerciseId}${sportQuery}`
					: `/profile/performance-profile/history/${encodedExerciseId}${sportQuery}`;
				const data = await fetchAPI(endpoint);
				if (active) {
					setHistoryData(data || null);
				}
			} catch (error) {
				if (active) {
					setHistoryData(null);
					setHistoryError(error?.message || t("historyLoadError"));
				}
			} finally {
				if (active) {
					setHistoryLoading(false);
				}
			}
		};

		loadHistory();
		return () => {
			active = false;
		};
	}, [athleteId, historyOpen, historyExercise, historyRevision, sport, t]);

	const entryKey = (entry) =>
		`${entry.result_id}:${entry.superset_detail_index ?? "direct"}`;
	const historyEndpoint = (entry) =>
		`/coach/athletes/${encodeURIComponent(athleteId)}/performance-profile/history/diary/${encodeURIComponent(entry.result_id)}`;

	const startEdit = (entry) => {
		setEditingKey(entryKey(entry));
		setDraft({
			date: entry.date || "",
			weight: String(entry.raw_weight ?? entry.value ?? ""),
			reps: String(entry.raw_reps ?? ""),
			value: String(entry.value ?? ""),
			notes: entry.notes || "",
		});
	};

	const saveEntry = async (entry) => {
		const key = entryKey(entry);
		const unit = historyData.unit;
		const numeric = (value) =>
			value.trim() === "" ? null : Number(value.replace(",", "."));
		const payload = {
			date: draft.date,
			notes: draft.notes,
			...(Number.isInteger(entry.superset_detail_index)
				? { superset_detail_index: entry.superset_detail_index }
				: {}),
			...(Number.isInteger(entry.result_set_row_index)
				? { result_set_row_index: entry.result_set_row_index }
				: {}),
		};
		if (unit === "kg") {
			payload.weight_kg = numeric(draft.weight);
			payload.reps = numeric(draft.reps);
			if (
				!Number.isFinite(payload.weight_kg) ||
				payload.weight_kg <= 0 ||
				!Number.isInteger(payload.reps) ||
				payload.reps < 1
			) {
				toast.error(t("historyInvalidValue"));
				return;
			}
		} else {
			const value = numeric(draft.value);
			if (
				value === null ||
				!Number.isFinite(value) ||
				value < 0 ||
				(["reps", "seconds", "minutes_seconds"].includes(unit) &&
					!Number.isInteger(value))
			) {
				toast.error(t("historyInvalidValue"));
				return;
			}
			if (unit === "reps") payload.reps = value;
			else if (["seconds", "minutes_seconds"].includes(unit))
				payload.time_seconds = value;
			else payload.score = value;
		}
		if (
			entry.affects_other_movements &&
			!window.confirm(t("historySharedEditConfirm"))
		)
			return;
		setSavingKey(key);
		try {
			await fetchAPI(historyEndpoint(entry), {
				method: "PUT",
				body: JSON.stringify(payload),
			});
			setEditingKey(null);
			setHistoryRevision((revision) => revision + 1);
			await onHistoryChanged?.();
			toast.success(t("historyEntryUpdated"));
		} catch (error) {
			toast.error(error.message || t("error"));
		} finally {
			setSavingKey(null);
		}
	};

	const deleteEntry = async (entry) => {
		if (
			!window.confirm(
				t(
					entry.affects_other_movements
						? "historySharedDeleteConfirm"
						: "historyDeleteConfirm",
				),
			)
		)
			return;
		const key = entryKey(entry);
		const query = Number.isInteger(entry.superset_detail_index)
			? `?superset_detail_index=${entry.superset_detail_index}`
			: "";
		setSavingKey(key);
		try {
			await fetchAPI(`${historyEndpoint(entry)}${query}`, { method: "DELETE" });
			setEditingKey(null);
			setHistoryRevision((revision) => revision + 1);
			await onHistoryChanged?.();
			toast.success(t("historyEntryDeleted"));
		} catch (error) {
			toast.error(error.message || t("error"));
		} finally {
			setSavingKey(null);
		}
	};

	const renderHistoryValue = (entry, unit) => {
		if (!entry) return "-";
		if (unit === "kg") {
			const weight = entry.raw_weight ?? entry.value;
			const reps = entry.raw_reps;
			if (reps !== null && reps !== undefined) {
				return `${weight || "-"} kg x ${reps}`;
			}
			return `${weight || "-"} kg`;
		}
		if (unit === "minutes_seconds") {
			return formatMinutesSeconds(entry.value);
		}
		// L'hit factor è un rapporto: senza decimali 8.01 e 8.49 diventerebbero
		// lo stesso numero, e su un drill sono due prestazioni distinte.
		if (unit === "hit_factor") {
			const parsed = Number(entry.value);
			return Number.isFinite(parsed) ? `HF ${parsed.toFixed(2)}` : "-";
		}
		if (unit === "seconds") {
			return `${entry.value || "-"} s`;
		}
		if (unit === "minutes") {
			return `${entry.value || "-"} min`;
		}
		if (unit === "reps") {
			return `${entry.value || "-"} reps`;
		}
		return entry.value ?? "-";
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center py-10 text-muted-foreground">
				<Loader2 className="w-5 h-5 animate-spin mr-2" />
				{t("loading")}
			</div>
		);
	}

	if (!domains.length) {
		// Due vuoti diversi: "nessun risultato" e "nessuna disciplina scelta".
		// Il secondo e' una configurazione mancante che blocca anche i massimali
		// manuali, e il coach deve poterlo distinguere a colpo d'occhio invece
		// di leggerlo come assenza di dati. `athleteSports` e' opzionale: senza,
		// resta il messaggio generico di prima.
		const sportMissing =
			Array.isArray(athleteSports) && athleteSports.length === 0;
		// Con piu' sport il vuoto e' di quel tab: la disciplina non ha ancora
		// domini configurati, mentre gli altri tab possono essere pieni.
		const sportWithoutDomains =
			Array.isArray(athleteSports) && athleteSports.length > 1;
		return (
			<Card className="bg-card border-border rounded-sm">
				<CardContent className="py-10 text-center">
					<LineChartIcon className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
					<p className="text-sm text-muted-foreground">
						{sportMissing
							? t("performanceNoSportCoachHint")
							: sportWithoutDomains
								? t("performanceNoDomainsForSport")
								: t("noPerformanceProfileData")}
					</p>
				</CardContent>
			</Card>
		);
	}

	return (
		<>
			<div className="space-y-4 w-full min-w-0">
				{/* Il totale IPF GL, nei modelli che lo usano (D6b). */}
				<IpfTotalLine domains={domains} />
				<div className="flex gap-1.5 overflow-x-auto pb-1 no-scrollbar max-w-full">
					<Button
						variant={activeTab === "results" ? "default" : "secondary"}
						size="sm"
						className={`rounded-sm shrink-0 ${
							activeTab === "results" ? "bg-volt hover:bg-volt/90" : ""
						}`}
						onClick={() => setActiveTab("results")}
					>
						{t("resultsAnalysis")}
					</Button>
					{domains.length > 1 && (
						<Button
							variant={activeTab === "radar" ? "default" : "secondary"}
							size="sm"
							className={`rounded-sm shrink-0 ${
								activeTab === "radar" ? "bg-volt hover:bg-volt/90" : ""
							}`}
							onClick={() => setActiveTab("radar")}
						>
							{t("performanceGeneralTab")}
						</Button>
					)}
					{domains.map((domain) => (
						<Button
							key={`coach_pp_tab_${domain.domain_id}`}
							variant={activeTab === domain.domain_id ? "default" : "secondary"}
							size="sm"
							className={`rounded-sm shrink-0 ${
								activeTab === domain.domain_id ? "bg-volt hover:bg-volt/90" : ""
							}`}
							onClick={() => setActiveTab(domain.domain_id)}
						>
							{domain.name}
						</Button>
					))}
				</div>

				{domains.length > 1 && activeTab === "radar" && (
					<Card className="bg-card border-border rounded-sm mb-3">
						<CardContent className="p-3">
							<p className="label-tactical mb-2">
								{t("performanceRadarTitle")}
							</p>
							{(() => {
								const radarData = domains.map((domain) => {
									const validScores = (domain.exercises || [])
										.map((exercise) => Number(exercise.score_value))
										.filter((value) => Number.isFinite(value));
									const avg =
										validScores.length > 0
											? validScores.reduce((sum, value) => sum + value, 0) /
												validScores.length
											: 0;
									return {
										domain: domain.name,
										score: avg,
									};
								});
								const radarMax = Math.max(
									15,
									...radarData.map((item) => item.score || 0),
								);

								return radarData.length >= 2 ? (
									<div className="h-72 w-full min-w-0">
										<ResponsiveContainer width="100%" height="100%">
											<RadarChart
												data={radarData}
												outerRadius="70%"
												margin={{ top: 12, right: 28, bottom: 12, left: 28 }}
											>
												<PolarGrid stroke={chartColors.gridSoft} />
												<PolarAngleAxis
													dataKey="domain"
													tick={{ fill: chartColors.axisMuted, fontSize: 10 }}
												/>
												<PolarRadiusAxis
													angle={30}
													domain={[0, radarMax]}
													tickCount={4}
													tick={{ fill: chartColors.axisFaint, fontSize: 10 }}
												/>
												<Radar
													name="Score"
													dataKey="score"
													stroke={chartColors.volt}
													fill={chartColors.volt}
													fillOpacity={0.35}
												/>
											</RadarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<p className="text-xs text-muted-foreground">
										{t("performanceRadarMissingConfig")}
									</p>
								);
							})()}
						</CardContent>
					</Card>
				)}

				{domains.length > 0 ? (
					activeTab !== "radar" &&
					visibleDomains.map((domain) => (
						<div key={domain.domain_id}>
							<div className="flex items-center gap-2 mb-2 min-w-0">
								<div
									className="w-2.5 h-5 rounded-sm shrink-0"
									style={{ backgroundColor: domain.color || chartColors.volt }}
								/>
								<p className="font-heading text-sm uppercase tracking-wider truncate">
									{domain.name}
								</p>
								{/* La descrizione sta nella guida: di fianco al nome era
								    troncata, e per alcuni modelli e' lunga. */}
								<LevelsGuideButton domain={domain} />
							</div>

							{activeTab !== "results" &&
								(() => {
									const radarItems = (domain.exercises || [])
										.filter(
											(exercise) =>
												exercise.score_value !== null &&
												exercise.score_value !== undefined &&
												Number.isFinite(Number(exercise.score_value)),
										)
										.map((exercise) => ({
											exercise: exercise.exercise_name,
											score: Number(exercise.score_value),
										}));

									const radarMax = Number(domain.scoring_max) || 15;

									return (
										<Card className="bg-card border-border rounded-sm mb-3">
											<CardContent className="p-3">
												<p className="label-tactical mb-2">
													{t("performanceRadarTitle")} {domain.name}
												</p>
												{radarItems.length >= 3 ? (
													<div className="h-72 w-full min-w-0">
														<ResponsiveContainer width="100%" height="100%">
															<RadarChart
																data={radarItems}
																outerRadius="70%"
																margin={{
																	top: 12,
																	right: 28,
																	bottom: 12,
																	left: 28,
																}}
															>
																<PolarGrid stroke={chartColors.gridSoft} />
																<PolarAngleAxis
																	dataKey="exercise"
																	tick={{ fill: chartColors.axisMuted, fontSize: 10 }}
																/>
																<PolarRadiusAxis
																	angle={30}
																	domain={[0, radarMax]}
																	tickCount={4}
																	tick={{ fill: chartColors.axisFaint, fontSize: 10 }}
																/>
																<Radar
																	name="Score"
																	dataKey="score"
																	stroke={readable(domain.color || chartColors.volt)}
																	fill={readable(domain.color || chartColors.volt)}
																	fillOpacity={0.35}
																/>
															</RadarChart>
														</ResponsiveContainer>
													</div>
												) : (
													<p className="text-xs text-muted-foreground">
														{t("performanceRadarMissingConfig")}
													</p>
												)}
											</CardContent>
										</Card>
									);
								})()}

							{domain.exercises?.length > 0 ? (
								<div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
									{domain.exercises.map((exercise) => {
										const isConditioning =
											exercise.exercise_id?.startsWith("cond_") ||
											exercise.conditioning_type;
										return (
											<Card
												key={exercise.config_id || exercise.exercise_id}
												className="bg-card border-border rounded-sm overflow-hidden"
											>
												<CardContent className="p-3">
													<div className="flex items-center justify-between gap-2 min-w-0">
														<div className="flex-1 min-w-0">
															{isConditioning ? (
																<div className="text-left group">
																	<p className="text-sm font-medium truncate group-hover:text-volt">
																		{exercise.exercise_name}
																	</p>
																	<div className="flex items-center gap-1 text-[10px] text-volt">
																		<ExternalLink className="w-3 h-3" />
																		<span>Tocca per dettagli</span>
																	</div>
																</div>
															) : (
																<p className="text-sm font-medium truncate">
																	{exercise.exercise_name}
																</p>
															)}

															{exercise.source && (
																<div className="space-y-1">
																	<p className="text-[10px] text-muted-foreground break-words">
																		{getPerformanceSourceLabel(exercise, t)}
																		{getPerformanceSourceMeta(exercise) ? (
																			<span className="ml-1 text-muted-foreground/70">
																				·{" "}
																				{formatDate(
																					getPerformanceSourceMeta(exercise),
																				)}
																			</span>
																		) : null}
																	</p>
																	{exercise.source_reason === "auto_highest" &&
																		exercise.manual_value != null && (
																			<p className="text-[10px] text-amber-400/80">
																				{t("sourceAutoHigher")}
																			</p>
																		)}
																	{exercise.source === "manual" &&
																		exercise.source_reason ===
																			"manual_highest" &&
																		exercise.auto_value != null && (
																			<p className="text-[10px] text-muted-foreground/70">
																				{t("sourceManualHigher")}
																			</p>
																		)}
																</div>
															)}
														</div>

														<div className="flex items-center gap-2 shrink-0">
															{exercise.value !== null &&
															exercise.value !== undefined ? (
																<div className="text-right">
																	<p
																		className="font-heading text-lg whitespace-nowrap"
																		style={{ color: readable(domain.color || chartColors.volt) }}
																	>
																		{exercise.unit === "minutes_seconds"
																			? formatMinutesSeconds(exercise.value)
																			: exercise.value}
																		<span className="text-xs text-muted-foreground ml-1">
																			{getUnitLabel(exercise.unit)}
																		</span>
																	</p>
																	{exercise.coefficient?.value != null && (
																		<p className="text-[10px] text-muted-foreground whitespace-nowrap">
																			{t(
																				exercise.coefficient.kind === "ipf_gl"
																					? "ppGlValue"
																					: "ppSinclairValue",
																			).replace("{value}", exercise.coefficient.value)}
																		</p>
																	)}
																	{classBased &&
																		exercise.scoring_basis &&
																		exercise.scoring_basis !== "weight_class" && (
																			<p className="text-[10px] text-amber-300/80 whitespace-nowrap">
																				{t("ppGenderTableUsed")}
																			</p>
																		)}
																</div>
															) : (
																<p className="text-sm text-muted-foreground italic">
																	—
																</p>
															)}
															<Button
																variant="ghost"
																size="icon"
																className="h-7 w-7 rounded-sm text-muted-foreground hover:text-volt"
																onClick={() => handleOpenHistory(exercise)}
																title={t("history")}
															>
																<Clock className="w-3.5 h-3.5" />
															</Button>
														</div>
													</div>
													{/* Il livello: prima si leggeva solo nel radar. */}
													<LevelBar
														exercise={exercise}
														domain={domain}
														color={readable(domain.color || chartColors.volt)}
													/>
												</CardContent>
											</Card>
										);
									})}
								</div>
							) : (
								<p className="text-xs text-muted-foreground py-2">
									{t("noPerformanceProfileData")}
								</p>
							)}
						</div>
					))
				) : (
					<p className="text-xs text-muted-foreground py-2">
						{t("noPerformanceProfileData")}
					</p>
				)}
			</div>

			<Dialog
				open={historyOpen}
				onOpenChange={(open) => {
					setHistoryOpen(open);
					if (!open) {
						setEditingKey(null);
						setHistoryExercise(null);
						setHistoryData(null);
						setHistoryError("");
					}
				}}
			>
				<DialogContent className="bg-card border-border max-w-2xl w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading flex items-center gap-2">
							<Clock className="w-4 h-4 text-volt" />
							{t("performanceHistoryTitle")}
						</DialogTitle>
						<p className="text-xs text-muted-foreground pr-6">
							{historyData?.exercise_name || historyExercise?.exercise_name}
							{historyData?.unit ? ` · ${getUnitLabel(historyData.unit)}` : ""}
						</p>
						<p className="text-xs text-muted-foreground">
							{t("performanceHistoryDescription")}
						</p>
					</DialogHeader>

					{historyLoading ? (
						<div className="flex justify-center py-8">
							<div className="spinner"></div>
						</div>
					) : historyError ? (
						<p className="text-sm text-red-400">
							{t("historyLoadError")}: {historyError}
						</p>
					) : historyData ? (
						<div className="space-y-3">
							{historyChartData.length > 1 && (
								<div className="space-y-2">
									{historyData.unit === "kg" && (
										<div className="flex flex-wrap items-center gap-3 text-[10px] text-muted-foreground">
											<div className="flex items-center gap-1.5">
												<span className="w-2.5 h-2.5 rounded-full bg-volt" />
												<span>{t("historyWeight")}</span>
											</div>
											<div className="flex items-center gap-1.5">
												<span className="w-2.5 h-2.5 rounded-full bg-acid" />
												<span>{t("historyEstimated1rm")}</span>
											</div>
										</div>
									)}
									<div className="h-[180px]">
										<ResponsiveContainer width="100%" height="100%">
											<LineChart data={historyChartData}>
												<CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
												<XAxis
													dataKey="date"
													stroke={chartColors.axis}
													tick={{ fill: chartColors.axis, fontSize: 10 }}
												/>
												<YAxis
													stroke={chartColors.axis}
													tick={{ fill: chartColors.axis, fontSize: 10 }}
													width={35}
												/>
												<RechartsTooltip
													contentStyle={tooltipStyle}
													formatter={(v, name, entry) => {
														if (historyData.unit === "kg") {
															const label =
																name === "weight"
																	? t("historyWeight")
																	: t("historyEstimated1rm");
															const repsSuffix =
																name === "weight" && entry?.payload?.reps
																	? ` x${entry.payload.reps}`
																	: "";
															return [`${v} kg${repsSuffix}`, label];
														}
														return [`${v}`, getUnitLabel(historyData.unit)];
													}}
												/>
												{historyData.unit === "kg" ? (
													<>
														<Line
															type="monotone"
															dataKey="weight"
															name="weight"
															stroke={chartColors.blue}
															strokeWidth={2}
															dot={{ fill: chartColors.blue, r: 3 }}
														/>
														<Line
															type="monotone"
															dataKey="estimated1rm"
															name="estimated1rm"
															stroke={chartColors.acid}
															strokeWidth={2}
															dot={{ fill: chartColors.acid, r: 3 }}
														/>
													</>
												) : (
													<Line
														type="monotone"
														dataKey="value"
														stroke={chartColors.volt}
														strokeWidth={2}
														dot={{ fill: chartColors.volt, r: 3 }}
													/>
												)}
											</LineChart>
										</ResponsiveContainer>
									</div>
								</div>
							)}

							{historyItems.length > 0 ? (
								<div className="space-y-2">
									{historyItems.map((entry, index) => (
										<div
											key={
												entry.result_id
													? entryKey(entry)
													: `${entry.entry_type}_${index}`
											}
											className="p-3 bg-secondary rounded-sm"
										>
											{editingKey === entryKey(entry) ? (
												<div className="space-y-2">
													{entry.affects_other_movements && (
														<p className="text-xs text-amber-400">
															{t("historySharedResultWarning")}
														</p>
													)}
													<Input
														type="date"
														aria-label={t("date")}
														value={draft.date}
														onChange={(event) =>
															setDraft({ ...draft, date: event.target.value })
														}
													/>
													<div className="flex gap-2">
														{historyData.unit === "kg" ? (
															<>
																<Input
																	type="number"
																	min="0.01"
																	step="any"
																	aria-label={t("historyWeight")}
																	value={draft.weight}
																	onChange={(event) =>
																		setDraft({
																			...draft,
																			weight: event.target.value,
																		})
																	}
																/>
																<Input
																	type="number"
																	min="1"
																	step="1"
																	aria-label={t("repsShort")}
																	value={draft.reps}
																	onChange={(event) =>
																		setDraft({
																			...draft,
																			reps: event.target.value,
																		})
																	}
																/>
															</>
														) : (
															<Input
																type="number"
																min="0"
																step="any"
																aria-label={getUnitLabel(historyData.unit)}
																value={draft.value}
																onChange={(event) =>
																	setDraft({
																		...draft,
																		value: event.target.value,
																	})
																}
															/>
														)}
													</div>
													<Input
														aria-label={t("notes")}
														placeholder={t("notes")}
														value={draft.notes}
														onChange={(event) =>
															setDraft({ ...draft, notes: event.target.value })
														}
													/>
													<div className="flex gap-2">
														<Button
															size="sm"
															disabled={savingKey === entryKey(entry)}
															onClick={() => saveEntry(entry)}
														>
															{t("saveChanges")}
														</Button>
														<Button
															size="sm"
															variant="outline"
															disabled={savingKey === entryKey(entry)}
															onClick={() => setEditingKey(null)}
														>
															{t("cancel")}
														</Button>
													</div>
												</div>
											) : (
												<div className="flex items-center justify-between gap-3">
													<div className="min-w-0">
														<p className="text-sm font-medium">
															{entry.date || "-"}
														</p>
														<p className="text-xs text-muted-foreground">
															{renderHistoryValue(entry, historyData.unit)}
														</p>
														<HistoryEntrySource
															entry={entry}
															onOpen={
																entry.result_id
																	? () => setSessionResultId(entry.result_id)
																	: undefined
															}
														/>
														{entry.notes && (
															<p className="text-xs text-muted-foreground mt-1 line-clamp-2">
																{entry.notes}
															</p>
														)}
													</div>
													<div className="flex items-center gap-1 shrink-0">
														<span className="text-[10px] uppercase tracking-wider text-muted-foreground">
															{entry.entry_type !== "manual"
																? t("fromDiary")
																: !entry.removed
																	? t("manualLabel")
																	: entry.removed_at
																		? `${t("manualRemovedOn")} ${formatDate(entry.removed_at)}`
																		: t("manualNoLongerActive")}
														</span>
														{entry.result_id && (
															<Button
																variant="ghost"
																size="icon"
																className="h-9 w-9 rounded-sm"
																title={t("sessionDetailButton")}
																aria-label={t("sessionDetailButton")}
																onClick={() =>
																	setSessionResultId(entry.result_id)
																}
															>
																<ClipboardList className="w-4 h-4" />
															</Button>
														)}
														{athleteId && entry.can_edit && (
															<Button
																variant="ghost"
																size="icon"
																className="h-9 w-9 rounded-sm"
																title={t("editResultTitle")}
																onClick={() => startEdit(entry)}
															>
																<Edit2 className="w-4 h-4" />
															</Button>
														)}
														{athleteId && entry.can_delete && (
															<Button
																variant="ghost"
																size="icon"
																className="h-9 w-9 rounded-sm text-blaze"
																title={t("deleteResultTitle")}
																disabled={savingKey === entryKey(entry)}
																onClick={() => deleteEntry(entry)}
															>
																<Trash2 className="w-4 h-4" />
															</Button>
														)}
													</div>
												</div>
											)}
										</div>
									))}
								</div>
							) : (
								<p className="text-sm text-muted-foreground">
									{t("noDataAvailable")}
								</p>
							)}
						</div>
					) : null}
				</DialogContent>
			</Dialog>

			<SessionDetailDialog
				open={!!sessionResultId}
				onOpenChange={(open) => {
					if (!open) setSessionResultId(null);
				}}
				resultId={sessionResultId}
				athleteId={athleteId}
				exerciseName={
					historyData?.exercise_name || historyExercise?.exercise_name
				}
			/>
		</>
	);
}
